import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageHeader } from '../hooks/usePageHeader'
import { createToken, listPasskeys, deletePasskey, renamePasskey, listTokens, revokeToken, sendPasskeyAddEmail, sendVerificationEmail } from '../api/auth'
import type { PasskeyInfo, TokenInfo } from '../api/auth'
import { sendInvite } from '../api/invites'
import { uploadEntry } from '../api/entries'
import { getUserSettings, updateUserSettings, getFollowRequests, handleFollowRequest } from '../api/users'
import type { FollowRequest } from '../api/users'
import { listSchemes, createScheme, updateScheme, deleteScheme } from '../api/schemes'
import type { ColorScheme } from '../api/schemes'
import { AvatarImage } from '../components/AvatarImage'
import { PALETTES, paletteToTheme, findMatchingPalette } from '../lib/palettes'
import { api } from '../api/client'
import { useAuthStore } from '../store/auth'
import { useThemeStore } from '../store/theme'
import { useDefaultsStore } from '../store/defaults'
import type { ColorTheme } from '../components/Entry/colorTheme'
import { COLOR_KEYS } from '../components/Entry/colorTheme'

interface IdentityCard {
  avatar_entry_id: string | null
  bio_entry_id: string | null
}

const POLICY_LABELS: Record<string, string> = {
  none: 'no one',
  friends: 'friends only',
  friends_of_friends: 'friends of friends',
  anyone: 'anyone',
}

const VISIBILITY_LABELS: Record<number, string> = {
  0: 'private',
  2: 'ask to follow',
  3: 'everyone',
}

function SchemeSwatches({ scheme }: { scheme: { bg: string; fg: string; color1: string; color2: string } }) {
  const colors = [scheme.bg, scheme.fg, scheme.color1, scheme.color2]
  return (
    <span style={{ display: 'inline-flex', gap: 2, verticalAlign: 'middle' }}>
      {colors.map((c, i) => (
        <span key={i} style={{ width: 14, height: 14, background: c, display: 'inline-block', border: '1px solid var(--color-fg)', opacity: 0.8 }} />
      ))}
    </span>
  )
}

export function Settings() {
  const { username, clearAuth } = useAuthStore()
  const { setUserTheme } = useThemeStore()
  const { setDefaults } = useDefaultsStore()
  const navigate = useNavigate()
  const [tokens, setTokens] = useState<TokenInfo[] | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [createdToken, setCreatedToken] = useState('')

  const [identity, setIdentity] = useState<IdentityCard | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const [visibility, setVisibility] = useState(0)
  const [followRequestPolicy, setFollowRequestPolicy] = useState('none')
  const [defaultEntryPrivacy, setDefaultEntryPrivacy] = useState(0)
  const [defaultReplyPrivacy, setDefaultReplyPrivacy] = useState(0)
  const [defaultUploadPrivacy, setDefaultUploadPrivacy] = useState(0)
  const [defaultTagsetPrivacy, setDefaultTagsetPrivacy] = useState(0)
  const [privacySaving, setPrivacySaving] = useState(false)
  const [privacySaved, setPrivacySaved] = useState(false)
  const [privacyError, setPrivacyError] = useState('')


  // Color scheme management
  const [schemes, setSchemes] = useState<ColorScheme[]>([])
  const [defaultSchemeId, setDefaultSchemeId] = useState<string | null>(null)
  const [defaultSelectValue, setDefaultSelectValue] = useState<string>('')
  const [editingSchemeId, setEditingSchemeId] = useState<string | null>(null)
  const [editSchemeName, setEditSchemeName] = useState('')
  const [editSchemeColors, setEditSchemeColors] = useState<ColorTheme | null>(null)
  const [newSchemeName, setNewSchemeName] = useState('')
  const [newSchemeColors, setNewSchemeColors] = useState<ColorTheme | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [schemeSaving, setSchemeSaving] = useState(false)
  const [schemeError, setSchemeError] = useState('')

  // Email verification
  const [email, setEmail] = useState('')
  const [emailVerified, setEmailVerified] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [emailError, setEmailError] = useState('')

  // Invite
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [inviteError, setInviteError] = useState('')

  // Passkey management
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null)
  const [passkeySending, setPasskeySending] = useState(false)
  const [passkeySent, setPasskeySent] = useState(false)
  const [passkeyError, setPasskeyError] = useState('')
  const [renamingPasskeyId, setRenamingPasskeyId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameSaving, setRenameSaving] = useState(false)

  useEffect(() => {
    if (!username) return
    Promise.all([
      api.get<IdentityCard>(`/users/${username}/identity`).then(setIdentity).catch(() => {}),
      listPasskeys().then(setPasskeys).catch(() => {}),
      api.get<{ email: string; email_verified: boolean }>(`/users/${username}/profile`).then((p) => {
        setEmail(p.email ?? '')
        setEmailVerified(p.email_verified ?? false)
      }).catch(() => {}),
      getUserSettings(username).then((s) => {
        setVisibility(s.visibility)
        setFollowRequestPolicy(s.follow_request_policy)
        setDefaultEntryPrivacy(s.default_entry_privacy)
        setDefaultReplyPrivacy(s.default_reply_privacy)
        setDefaultUploadPrivacy(s.default_upload_privacy)
        setDefaultTagsetPrivacy(s.default_tagset_privacy)
        setNotifyFollowRequestsEmail(s.notify_follow_requests_email)
        setDefaultSchemeId(s.default_scheme_id)
        setDefaultSelectValue(s.default_scheme_id ?? findMatchingPalette(s.color_theme) ?? '')
        if (s.color_theme) {
          setUserTheme(s.color_theme)
        } else {
          const localTheme = useThemeStore.getState().userTheme
          if (localTheme) {
            updateUserSettings(username, { color_theme: localTheme }).catch(() => {})
          }
        }
      }).catch(() => {}),
      listSchemes(username).then(setSchemes).catch(() => {}),
    ])
  }, [username, setUserTheme])

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !username) return
    setAvatarUploading(true)
    setAvatarError('')
    try {
      const entry = await uploadEntry(file, file.name, 3)
      const updated = await api.put<IdentityCard>(`/users/${username}/identity`, { avatar_entry_id: entry.id })
      setIdentity(updated)
      if (avatarInputRef.current) avatarInputRef.current.value = ''
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setAvatarUploading(false)
    }
  }

  const [notifyFollowRequestsEmail, setNotifyFollowRequestsEmail] = useState(false)
  const [notifySaving, setNotifySaving] = useState(false)
  const [notifySaved, setNotifySaved] = useState(false)

  const [followRequests, setFollowRequests] = useState<FollowRequest[] | null>(null)
  const [followRequestsLoading, setFollowRequestsLoading] = useState(false)

  useEffect(() => {
    if (!username) return
    setFollowRequestsLoading(true)
    getFollowRequests(username)
      .then(setFollowRequests)
      .catch(() => {})
      .finally(() => setFollowRequestsLoading(false))
  }, [username])

  async function handleSendVerification() {
    setEmailSending(true)
    setEmailError('')
    setEmailSent(false)
    try {
      await sendVerificationEmail()
      setEmailSent(true)
    } catch (err) {
      setEmailError(err instanceof Error ? err.message || 'failed to send' : 'failed to send')
    } finally {
      setEmailSending(false)
    }
  }

  async function handleDeletePasskey(id: string) {
    setPasskeyError('')
    try {
      await deletePasskey(id)
      setPasskeys((prev) => prev ? prev.filter((p) => p.id !== id) : prev)
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message : 'failed to delete passkey')
    }
  }

  async function handleRenamePasskey(id: string) {
    setRenameSaving(true)
    setPasskeyError('')
    try {
      const updated = await renamePasskey(id, renameValue)
      setPasskeys((prev) => prev ? prev.map((p) => p.id === id ? updated : p) : prev)
      setRenamingPasskeyId(null)
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message : 'failed to rename passkey')
    } finally {
      setRenameSaving(false)
    }
  }

  async function handleSendInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviteSending(true)
    setInviteError('')
    setInviteSent(false)
    try {
      await sendInvite(inviteEmail.trim())
      setInviteSent(true)
      setInviteEmail('')
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'failed to send invite')
    } finally {
      setInviteSending(false)
    }
  }

  async function handleSendPasskeyLink() {
    setPasskeySending(true)
    setPasskeyError('')
    setPasskeySent(false)
    try {
      await sendPasskeyAddEmail()
      setPasskeySent(true)
    } catch (err) {
      setPasskeyError(err instanceof Error ? err.message || 'failed to send' : 'failed to send')
    } finally {
      setPasskeySending(false)
    }
  }

  async function respondToFollowRequest(reqId: string, action: 'approve' | 'reject') {
    if (!username) return
    await handleFollowRequest(username, reqId, action)
    setFollowRequests((reqs) => reqs ? reqs.filter((r) => r.id !== reqId) : reqs)
  }

  async function saveNotifySettings(value: boolean) {
    if (!username) return
    setNotifySaving(true)
    setNotifySaved(false)
    try {
      await updateUserSettings(username, { notify_follow_requests_email: value })
      setNotifyFollowRequestsEmail(value)
      setNotifySaved(true)
    } finally {
      setNotifySaving(false)
    }
  }

  async function loadTokens() {
    const ts = await listTokens()
    setTokens(ts)
  }

  async function handleCreateToken(e: React.FormEvent) {
    e.preventDefault()
    const result = await createToken(newLabel || 'API token')
    setCreatedToken(result.token)
    setNewLabel('')
    loadTokens()
  }

  async function handleRevoke(id: string) {
    await revokeToken(id)
    loadTokens()
  }

  async function savePrivacySettings() {
    if (!username) return
    setPrivacySaving(true)
    setPrivacyError('')
    setPrivacySaved(false)
    try {
      await updateUserSettings(username, {
        visibility,
        follow_request_policy: followRequestPolicy,
        default_entry_privacy: defaultEntryPrivacy,
        default_reply_privacy: defaultReplyPrivacy,
        default_upload_privacy: defaultUploadPrivacy,
        default_tagset_privacy: defaultTagsetPrivacy,
      })
      setDefaults({
        entry: defaultEntryPrivacy,
        reply: defaultReplyPrivacy,
        upload: defaultUploadPrivacy,
        tagset: defaultTagsetPrivacy,
      })
      setPrivacySaved(true)
    } catch (err) {
      setPrivacyError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setPrivacySaving(false)
    }
  }

  async function handleSetDefaultScheme(value: string | null) {
    if (!username) return
    setDefaultSelectValue(value ?? '')
    if (value && value.startsWith('builtin:')) {
      const palette = PALETTES.find((p) => p.id === value.slice(8))
      if (!palette) return
      const t = paletteToTheme(palette)
      await updateUserSettings(username, { color_theme: t, default_scheme_id: null }).catch(() => {})
      setDefaultSchemeId(null)
      setUserTheme(t)
    } else {
      await updateUserSettings(username, { default_scheme_id: value || null }).catch(() => {})
      setDefaultSchemeId(value || null)
      if (value) {
        const scheme = schemes.find((s) => s.id === value)
        if (scheme) {
          const t = { bg: scheme.bg, fg: scheme.fg, color1: scheme.color1, color2: scheme.color2 }
          setUserTheme(t)
          updateUserSettings(username, { color_theme: t }).catch(() => {})
        }
      }
    }
  }

  async function handleCreateScheme(e: React.FormEvent) {
    e.preventDefault()
    if (!username || !newSchemeName.trim() || !newSchemeColors) return
    setSchemeSaving(true)
    setSchemeError('')
    try {
      const scheme = await createScheme(username, { name: newSchemeName.trim(), ...newSchemeColors })
      setSchemes((prev) => [...prev, scheme].sort((a, b) => a.name.localeCompare(b.name)))
      setShowCreateForm(false)
      setNewSchemeName('')
      setNewSchemeColors(null)
    } catch (err) {
      setSchemeError(err instanceof Error ? err.message : 'Failed to create scheme')
    } finally {
      setSchemeSaving(false)
    }
  }

  async function handleUpdateScheme(schemeId: string) {
    if (!username || !editSchemeColors) return
    setSchemeSaving(true)
    setSchemeError('')
    try {
      const updated = await updateScheme(username, schemeId, { name: editSchemeName, ...editSchemeColors })
      setSchemes((prev) => prev.map((s) => s.id === schemeId ? updated : s).sort((a, b) => a.name.localeCompare(b.name)))
      setEditingSchemeId(null)
    } catch (err) {
      setSchemeError(err instanceof Error ? err.message : 'Failed to update scheme')
    } finally {
      setSchemeSaving(false)
    }
  }

  async function handleDeleteScheme(schemeId: string) {
    if (!username) return
    try {
      await deleteScheme(username, schemeId)
      setSchemes((prev) => prev.filter((s) => s.id !== schemeId))
      if (defaultSchemeId === schemeId) setDefaultSchemeId(null)
    } catch {
      setSchemeError('failed to delete scheme')
    }
  }

  usePageHeader('settings')

  return (
    <div>

      <section style={{ marginBottom: '2rem' }}>
        <h3>account</h3>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
          <button type="button" onClick={() => navigate(`/~${username}`)} style={{ width: 'auto' }}>view profile</button>
          <button type="button" onClick={() => navigate('/archive')} style={{ width: 'auto' }}>archive</button>
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>avatar</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem' }}>
          {identity?.avatar_entry_id
            ? <AvatarImage entryId={identity.avatar_entry_id} size={72} />
            : <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--color-fg)', opacity: 0.15 }} />
          }
          <div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              disabled={avatarUploading}
              onChange={handleAvatarChange}
              style={{ fontSize: '0.85rem' }}
            />
            {avatarUploading && <p style={{ opacity: 0.6, fontSize: '0.85rem', margin: '0.25rem 0 0' }}>uploading…</p>}
            {avatarError && <p style={{ color: 'var(--color-1)', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>{avatarError}</p>}
          </div>
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>color schemes</h3>


        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.5rem' }}>
          {PALETTES.map((palette) => {
            const builtinValue = `builtin:${palette.id}`
            const isDefault = defaultSelectValue === builtinValue
            return (
              <li key={palette.id} style={{ marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid var(--color-fg)', opacity: 0.85 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <SchemeSwatches scheme={palette} />
                  <span style={{ fontSize: '0.85rem', flex: 1 }}>{palette.name}</span>
                  <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>built-in</span>
                  {isDefault && <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>default</span>}
                  <button
                    type="button"
                    onClick={() => handleSetDefaultScheme(builtinValue)}
                    style={{ fontSize: '0.7rem', width: 'auto' }}
                  >use</button>
                </div>
              </li>
            )
          })}
        </ul>

        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem' }}>
          {schemes.map((scheme) => (
            <li key={scheme.id} style={{ marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid var(--color-fg)', opacity: 0.85 }}>
              {editingSchemeId === scheme.id ? (
                <div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                    <input
                      value={editSchemeName}
                      onChange={(e) => setEditSchemeName(e.target.value)}
                      style={{ fontSize: '0.85rem', width: 160 }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                    {editSchemeColors && COLOR_KEYS.map(({ key, label }) => (
                      <label key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: '0.65rem', cursor: 'pointer' }}>
                        <input
                          type="color"
                          value={editSchemeColors[key]}
                          onChange={(e) => setEditSchemeColors((c) => c ? { ...c, [key]: e.target.value } : c)}
                          style={{ width: 24, height: 24, padding: 0, border: '1px solid var(--color-fg)', borderRadius: 2, cursor: 'pointer', background: 'none' }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button type="button" onClick={() => handleUpdateScheme(scheme.id)} disabled={schemeSaving} style={{ fontSize: '0.75rem', width: 'auto' }}>save</button>
                    <button type="button" onClick={() => { setEditingSchemeId(null); setSchemeError('') }} style={{ fontSize: '0.75rem', width: 'auto' }}>cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <SchemeSwatches scheme={scheme} />
                  <span style={{ fontSize: '0.85rem', flex: 1 }}>{scheme.name}</span>
                  {defaultSchemeId === scheme.id && (
                    <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>default</span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleSetDefaultScheme(scheme.id)}
                    style={{ fontSize: '0.7rem', width: 'auto' }}
                  >use</button>
                  <button
                    type="button"
                    onClick={() => { setEditingSchemeId(scheme.id); setEditSchemeName(scheme.name); setEditSchemeColors({ bg: scheme.bg, fg: scheme.fg, color1: scheme.color1, color2: scheme.color2 }) }}
                    style={{ fontSize: '0.7rem', width: 'auto' }}
                  >edit</button>
                  <button
                    type="button"
                    onClick={() => handleDeleteScheme(scheme.id)}
                    style={{ fontSize: '0.7rem', width: 'auto', opacity: 0.6 }}
                  >delete</button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {!showCreateForm ? (
          <button type="button" onClick={() => { setShowCreateForm(true); setNewSchemeColors({ bg: '#0d0d0d', fg: '#e8e8e8', color1: '#c0392b', color2: '#2980b9' }); setSchemeError('') }} style={{ width: 'auto', fontSize: '0.85rem' }}>
            + new scheme
          </button>
        ) : (
          <form onSubmit={handleCreateScheme} style={{ border: '1px solid var(--color-fg)', padding: '0.75rem' }}>
            <input
              value={newSchemeName}
              onChange={(e) => setNewSchemeName(e.target.value)}
              placeholder="scheme name"
              style={{ marginBottom: '0.5rem', fontSize: '0.85rem' }}
            />
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              {newSchemeColors && COLOR_KEYS.map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: '0.65rem', cursor: 'pointer' }}>
                  <input
                    type="color"
                    value={newSchemeColors[key]}
                    onChange={(e) => setNewSchemeColors((c) => c ? { ...c, [key]: e.target.value } : c)}
                    style={{ width: 24, height: 24, padding: 0, border: '1px solid var(--color-fg)', borderRadius: 2, cursor: 'pointer', background: 'none' }}
                  />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" disabled={schemeSaving} style={{ width: 'auto', fontSize: '0.85rem' }}>create</button>
              <button type="button" onClick={() => setShowCreateForm(false)} style={{ width: 'auto', fontSize: '0.85rem' }}>cancel</button>
            </div>
            {schemeError && <p style={{ color: 'var(--color-1)', fontSize: '0.8rem', margin: '0.35rem 0 0' }}>{schemeError}</p>}
          </form>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>privacy</h3>
        <div style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
            who can see your profile?
          </label>
          <select
            value={visibility}
            onChange={(e) => { setVisibility(Number(e.target.value)); setPrivacySaved(false) }}
          >
            {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
            who can send you follow requests?
          </label>
          <select
            value={followRequestPolicy}
            onChange={(e) => { setFollowRequestPolicy(e.target.value); setPrivacySaved(false) }}
          >
            {Object.entries(POLICY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '0.75rem', marginTop: '1rem' }}>
          <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0 0 0.5rem' }}>default privacy for new content</p>
          {([
            ['entries', defaultEntryPrivacy, setDefaultEntryPrivacy],
            ['replies', defaultReplyPrivacy, setDefaultReplyPrivacy],
            ['uploads', defaultUploadPrivacy, setDefaultUploadPrivacy],
          ] as [string, number, (v: number) => void][]).map(([label, value, setter]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
              <span style={{ fontSize: '0.9rem', minWidth: 70 }}>{label}</span>
              <select
                value={value}
                onChange={(e) => { setter(Number(e.target.value)); setPrivacySaved(false) }}
                style={{ width: 'auto' }}
              >
                <option value={0}>private</option>
                <option value={1}>friends</option>
                <option value={2}>friends of friends</option>
                <option value={3}>public</option>
              </select>
            </div>
          ))}
        </div>
        <div>
          <button type="button" onClick={savePrivacySettings} disabled={privacySaving}>
            {privacySaving ? 'saving…' : 'save'}
          </button>
          {privacySaved && <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>saved</span>}
          {privacyError && <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: 'var(--color-1)' }}>{privacyError}</span>}
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>follow requests</h3>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={notifyFollowRequestsEmail}
              disabled={notifySaving}
              onChange={(e) => saveNotifySettings(e.target.checked)}
            />
            email me about new follow requests
          </label>
          {notifySaved && <span style={{ marginLeft: '1.5rem', fontSize: '0.8rem', opacity: 0.6 }}>saved</span>}
        </div>
        {followRequestsLoading && <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>loading…</p>}
        {!followRequestsLoading && followRequests !== null && followRequests.length === 0 && (
          <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>no pending follow requests</p>
        )}
        {followRequests && followRequests.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {followRequests.map((req) => (
              <li key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0' }}>
                <span style={{ fontSize: '0.9rem' }}>~{req.requester_username}</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => respondToFollowRequest(req.id, 'approve')} style={{ fontSize: '0.75rem' }}>approve</button>
                  <button type="button" onClick={() => respondToFollowRequest(req.id, 'reject')} style={{ fontSize: '0.75rem' }}>reject</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>email</h3>
        {email && (
          <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            {email}{' '}
            <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>
              ({emailVerified ? 'verified' : 'unverified'})
            </span>
          </p>
        )}
        {!emailVerified && (
          <div>
            <button
              type="button"
              onClick={handleSendVerification}
              disabled={emailSending || emailSent}
            >
              {emailSending ? 'sending…' : emailSent ? 'email sent' : 'send verification email'}
            </button>
            {emailSent && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
                check your inbox for a verification link
              </span>
            )}
            {emailError && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: 'var(--color-1)' }}>
                {emailError}
              </span>
            )}
          </div>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>invite</h3>
        <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0.25rem 0 0.75rem' }}>
          send someone an email invite with a link to this server
        </p>
        <form onSubmit={handleSendInvite} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="email"
            placeholder="email address"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); setInviteSent(false); setInviteError('') }}
            style={{ flex: 1 }}
          />
          <button type="submit" disabled={inviteSending || !inviteEmail.trim()} style={{ width: 'auto' }}>
            {inviteSending ? 'sending…' : 'send invite'}
          </button>
        </form>
        {inviteSent && (
          <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: '0.35rem 0 0' }}>invite sent</p>
        )}
        {inviteError && (
          <p style={{ fontSize: '0.85rem', color: 'var(--color-1)', margin: '0.35rem 0 0' }}>{inviteError}</p>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>passkeys</h3>
        {passkeys !== null && passkeys.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
            {passkeys.map((p) => (
              <li key={p.id} style={{ padding: '0.35rem 0', borderBottom: '1px solid var(--color-fg)', opacity: 0.85 }}>
                {renamingPasskeyId === p.id ? (
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      style={{ fontSize: '0.85rem', flex: 1 }}
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenamePasskey(p.id); if (e.key === 'Escape') setRenamingPasskeyId(null) }}
                    />
                    <button type="button" onClick={() => handleRenamePasskey(p.id)} disabled={renameSaving} style={{ fontSize: '0.75rem', width: 'auto' }}>save</button>
                    <button type="button" onClick={() => setRenamingPasskeyId(null)} style={{ fontSize: '0.75rem', width: 'auto' }}>cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem' }}>
                      {p.device_name || 'unnamed device'}
                      {p.last_used_at && (
                        <span style={{ fontSize: '0.75rem', opacity: 0.5, marginLeft: '0.5rem' }}>
                          last used {new Date(p.last_used_at).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <button
                        type="button"
                        onClick={() => { setRenamingPasskeyId(p.id); setRenameValue(p.device_name || '') }}
                        style={{ fontSize: '0.75rem', width: 'auto' }}
                      >
                        rename
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeletePasskey(p.id)}
                        style={{ fontSize: '0.75rem', width: 'auto', opacity: 0.6 }}
                      >
                        remove
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <p style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0.25rem 0 0.75rem' }}>
          add a new passkey to your account via an email link
        </p>
        <button
          type="button"
          onClick={handleSendPasskeyLink}
          disabled={passkeySending || passkeySent}
        >
          {passkeySending ? 'sending…' : passkeySent ? 'link sent' : `send passkey link to ${email || 'your email'}`}
        </button>
        {passkeySent && (
          <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
            check your inbox and click the link to add a new passkey
          </span>
        )}
        {passkeyError && (
          <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: 'var(--color-1)' }}>
            {passkeyError}
          </span>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>bookmarklet</h3>
        <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: '0.5rem 0' }}>
          drag this link to your bookmarks bar. clicking it on any page opens a new journal entry pre-filled with a link to that page.
        </p>
        {username && (() => {
          const origin = window.location.origin
          const href = `javascript:(function(){var t=document.title,u=location.href;window.open('${origin}/~${username}/journal?body='+encodeURIComponent('['+t+']('+u+')'),'_blank');})();`
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
              <a href={href} style={{ padding: '0.3rem 0.6rem', border: '1px solid var(--color-fg)', fontSize: '0.85rem', textDecoration: 'none', cursor: 'grab', whiteSpace: 'nowrap' }}>
                + journal entry
              </a>
              <button
                type="button"
                style={{ fontSize: '0.75rem' }}
                onClick={() => navigator.clipboard.writeText(href)}
              >
                copy code
              </button>
            </div>
          )
        })()}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3>api tokens</h3>
        {tokens === null && <button type="button" onClick={loadTokens}>load tokens</button>}
        {tokens !== null && (
          <>
            <form onSubmit={handleCreateToken} style={{ display: 'flex', gap: '0.5rem', margin: '0.5rem 0' }}>
              <input placeholder="token label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} style={{ flex: 1 }} />
              <button type="submit">create</button>
            </form>
            {createdToken && (
              <p style={{ background: '#1a1a1a', padding: '0.5rem', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                <strong>copy now — shown once:</strong> {createdToken}
              </p>
            )}
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {tokens.map((t) => (
                <li key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                  <span style={{ fontSize: '0.9rem' }}>{t.label} <span style={{ opacity: 0.5 }}>({t.client_type})</span></span>
                  <button type="button" onClick={() => handleRevoke(t.id)} style={{ fontSize: '0.75rem' }}>revoke</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section>
        <button type="button" onClick={() => { clearAuth(); navigate('/login') }}>
          logout
        </button>
      </section>

      <p style={{ marginTop: '3rem', fontSize: '0.7rem', opacity: 0.3 }}>v{__APP_VERSION__}</p>
    </div>
  )
}

